import React, { useEffect, useState, forwardRef, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Divider,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import validationRules, {
  maxLengthValidation,
} from '../../utils/validations.util';
import Iconify from '../common/iconify.component';
import Loading from '../table-elements/loading.component';
import SnackbarInfo from '../common/snackbar-info.component';
import {
  createNewCategory,
  viewCategoryById,
  updateCategoryById,
} from '../../services/content.service';
import SearchableChannel from '../form-elements/searchableChannel.component';
import Status from '../form-elements/status.component';
import CardType from '../form-elements/cardTypes.component';
import { getCardLabel } from '../../config/cardtype.config';
import { CONTENT_ALIAS } from '../../config/const.config';
import ConfirmPopup from '../common/confirm-popup.component';

const Transition = forwardRef((props, ref) => (
  <Slide direction="up" ref={ref} {...props} />
));

const AddEditCategories = ({ title, dataId, onCancel, onSuccess }) => {
  const formData = useRef(null);
  const categoryData = useRef(null);
  const confirmationMessage = useRef('');
  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
  } = useForm({
    defaultValues: {
      title: '',
      status: null,
      description: '',
      backgroundImage: '',
      channels: [],
      cardType: null,
      showOnHome: true,
    },
  });

  const [loading, setLoading] = useState(true);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [isConfirmationPromptOpen, setIsConfirmationPromptOpen] =
    useState(false);

  const saveNewData = (payload) => {
    createNewCategory(payload)
      .then(() => {
        onSuccess(`New ${CONTENT_ALIAS.CATEGORY_SINGULAR} added successfully.`);
        formData.current = null;
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
        formData.current = null;
      });
  };

  const updateExistingData = (payload) => {
    updateCategoryById(dataId, payload)
      .then(() => {
        onSuccess(
          `${CONTENT_ALIAS.CATEGORY_SINGULAR} details updated successfully.`
        );
        formData.current = null;
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setFormSubmitted(false);
        formData.current = null;
      });
  };

  const onFormSubmit = (data) => {
    setFormSubmitted(true);
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const finalData = data || formData.current;
    const channels = [];
    finalData.channels.forEach((d) => {
      if (typeof d === 'object') {
        channels.push(d.id);
      } else {
        channels.push(d);
      }
    });
    const payload = {
      title: finalData.title.trim(),
      status: finalData.status,
      description: finalData.description.trim(),
      backgroundImage: data.backgroundImage || '',
      channelId: channels || [],
      cardType: finalData.cardType.id,
      showOnHome: finalData.showOnHome,
    };

    if (formData.current && formData.current.resetVideos)
      payload.resetVideos = true;
    if (dataId) {
      updateExistingData(payload);
    } else {
      saveNewData(payload);
    }
  };

  useEffect(() => {
    if (!dataId) {
      setLoading(false);
      return;
    }

    viewCategoryById(dataId)
      .then((res) => {
        categoryData.current = res.data;

        // Fill form values
        setValue('title', res.data?.title || '');
        setValue('status', res.data?.status || 0);
        setValue('description', res.data?.description || '');
        setValue('backgroundImage', res.data?.backgroundImage || '');
        setValue('channels', res.data?.channels || []);
        setValue(
          'cardType',
          { id: res.data?.cardType, title: getCardLabel(res.data?.cardType) } ||
            null
        );
        setValue('showOnHome', !!res.data?.showOnHome);
        setLoading(false);
      })
      .catch((err) => {
        const message =
          err.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        setLoading(false);
      });
  }, [dataId]);

  return (
    <>
      <Dialog
        open
        aria-labelledby="add-dialog-title"
        aria-describedby="add-dialog-description"
        TransitionComponent={Transition}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle id="view-dialog-title">{title}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={onCancel}
          size="small"
          sx={{
            position: 'absolute',
            right: 10,
            top: 15,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <Iconify icon="ic:outline-close" />
        </IconButton>

        <DialogContent dividers id="add-dialog-description">
          {loading && <Loading />}

          {!loading && (
            <form id="add-edit-form" onSubmit={handleSubmit(onFormSubmit)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="title"
                    control={control}
                    rules={{
                      ...validationRules.REQUIRED,
                      ...{ ...maxLengthValidation(255) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={`${CONTENT_ALIAS.CATEGORY_SINGULAR} Name`}
                        type="text"
                        error={!!errors.title}
                        helperText={errors?.title?.message || null}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="status"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <Status
                        id="sub_status"
                        name="sub_status"
                        label="Status"
                        defaultValue={value}
                        onChange={onChange}
                        validOptions={[1, 0]}
                        sx={{ width: '100%' }}
                        error={errors?.status?.message || ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Controller
                    name="description"
                    control={control}
                    rules={{
                      ...{ ...maxLengthValidation(1500) },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Description"
                        type="text"
                        error={!!errors.description}
                        helperText={errors?.description?.message || null}
                        fullWidth
                        multiline
                        rows={3}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="cardType"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <CardType
                        id="cardType"
                        name="cardType"
                        label="Card Type"
                        defaultValue={value}
                        onChange={onChange}
                        validOptions={[1, 0]}
                        sx={{ width: '100%' }}
                        error={errors?.cardType?.message || ''}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="channels"
                    control={control}
                    rules={validationRules.REQUIRED}
                    render={({ field: { onChange, value } }) => (
                      <SearchableChannel
                        id="channels"
                        name="channels"
                        label={`Select ${CONTENT_ALIAS.CHANNEL_PLURAL}`}
                        defaultValue={value}
                        multiple
                        onChange={onChange}
                        sx={{ width: '100%' }}
                        error={errors?.channels?.message || ''}
                        disabled={dataId}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={6}>
                  <Controller
                    name="showOnHome"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            aria-label="showOnHome"
                            color="primary"
                            checked={value}
                            onChange={onChange}
                          />
                        }
                        label="Show On Home?"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={12} md={12}>
                  <Divider
                    textAlign="center"
                    variant="fullWidth"
                    sx={{
                      fontWeight: 'bold',
                    }}
                  >
                    {`${CONTENT_ALIAS.CATEGORY_SINGULAR} Details (You can upload images from upload section)`}
                  </Divider>
                </Grid>

                <Grid item xs={12} sm={12} md={12}>
                  <Controller
                    name="backgroundImage"
                    control={control}
                    rules={{
                      ...validationRules.OPTIONAL_URL,
                      ...{ ...maxLengthValidation(250) },
                      ...validationRules.IMAGE_EXTENSION,
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Background Image Url"
                        type="text"
                        error={!!errors.backgroundImage}
                        helperText={errors?.backgroundImage?.message || null}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'left', marginLeft: 2 }}>
          <Button
            type="submit"
            form="add-edit-form"
            color="primary"
            sx={{
              '&:hover': {
                color: 'white', // text color on hover
              },
            }}
            variant="contained"
            disabled={loading || formSubmitted}
          >
            Save
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={onCancel}
            disabled={formSubmitted}
          >
            Cancel
          </Button>
        </DialogActions>

        {snackbarInfo.show && (
          <SnackbarInfo
            type={snackbarInfo.type}
            message={snackbarInfo.message}
          />
        )}
      </Dialog>
      {isConfirmationPromptOpen && (
        <ConfirmPopup
          title="Confirm Action"
          message={confirmationMessage.current}
          onCancel={() => setIsConfirmationPromptOpen(false)}
          onSuccess={() => {
            onFormSubmit();
            setIsConfirmationPromptOpen(false);
          }}
          cancelButtonText="No"
          successButtonText="Yes"
        />
      )}
    </>
  );
};

AddEditCategories.propTypes = {
  title: PropTypes.string.isRequired,
  dataId: PropTypes.number.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
};

export default AddEditCategories;
